import React, { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { all_routes } from "../../router/all_routes";
import {
  NO_DATA,
  collectSv4Documents,
  collectSv4Remarks,
  findOneSv4Card,
  formatRemarkHistoryTimestamp,
  formatSv4DetailTimestamp,
  getDocumentDisplayName,
  getLoggedInUser,
  getOrganisationProjDisplay,
  getReasonDisplayLabel,
  getStatusInfo,
  getSv4ApproverNameDisplay,
  getSv4AttachmentUrl,
} from "./sv4Card.service";

export { getStatusInfo } from "./sv4Card.service";

export const InfoRow: React.FC<{ label: string; value?: string | React.ReactNode }> = ({
  label,
  value,
}) => (
  <div className="col-md-4 col-sm-6">
    <label className="form-label text-muted small mb-1">{label}</label>
    <div className="fw-semibold text-dark">{value || NO_DATA}</div>
  </div>
);

export const SectionCard: React.FC<{ icon: string; title: string; children: React.ReactNode }> = ({
  icon,
  title,
  children,
}) => (
  <div className="card border-0 shadow-sm mb-4">
    <div className="card-header bg-white py-3 border-bottom">
      <h5 className="card-title mb-0 fw-bold text-dark d-flex align-items-center">
        <i className={`${icon} me-2 text-danger fs-5`} />
        {title}
      </h5>
    </div>
    <div className="card-body p-4">
      <div className="row g-3">{children}</div>
    </div>
  </div>
);

interface DetailData {
  id?: number;
  SequenceNumber?: string;
  ReferenceNumber?: string;
  EmpID?: string;
  EmployeeName?: string;
  Designation?: string;
  Department?: string;
  EmployeeGroup?: string;
  PlaceOfPosting?: string;
  ContactNumber?: string;
  ExistingCardNumber?: string;
  RequestStatus?: string;
  Status?: string;
  RequestCreationDate?: string;
  SubmissionDate?: string;
  SubmissionDateDisplay?: string;
  TimeStamp?: string;
  WorkflowId?: string;
  OrganisationKey?: string;
  ReasonToApply?: string;
  ApproverUserID?: string;
  LatestApproverName?: string;
  ApproverName?: string;
  fullName?: string;
  ApproverLevel?: string;
  UserRemarks?: string;
  HRemarks?: string;
  ToDocuments?: { results?: any[] } | any[];
  ToPicture?: { results?: any[] } | any[];
  RemarksHistory?: any[];
  ToRemarks?: any[];
  ToEnables?: any[];
  approver_details?: any;
  applicant_details?: any;
}

export const DetailCards: React.FC<{ detail: DetailData }> = ({ detail }) => {
  const statusCode = detail.RequestStatus || "";
  const statusInfo = getStatusInfo(statusCode);
  const profileWingName = getLoggedInUser()?.details?.sap_department_designation_wings?.wing_name;
  const reasonLabel = getReasonDisplayLabel(detail.ReasonToApply || "", NO_DATA);
  const creationDisplay =
    formatSv4DetailTimestamp(detail.TimeStamp) ||
    formatSv4DetailTimestamp(detail.RequestCreationDate) ||
    NO_DATA;
  const approverNameDisplay = getSv4ApproverNameDisplay(detail, NO_DATA);
  const organisationProjDisplay = getOrganisationProjDisplay(
    detail.OrganisationKey,
    profileWingName,
    NO_DATA,
  );
  const documents = collectSv4Documents(detail);
  const remarksList = collectSv4Remarks(detail);

  const employeeName =
    detail.EmployeeName ||
    (detail.applicant_details
      ? `${detail.applicant_details.firstname || ""} ${detail.applicant_details.lsastname || detail.applicant_details.lastname || ""}`.trim()
      : "") ||
    NO_DATA;

  return (
    <>
      <div className="card border-0 shadow-sm mb-4">
        <div className="card-body d-flex align-items-center justify-content-between flex-wrap gap-2 py-3 px-4">
          <div>
            <h4 className="mb-1 fw-bold text-dark d-flex align-items-center gap-2">
              <i className="ti ti-file-certificate text-danger fs-3" />
              Request Status
            </h4>
            <p className="text-muted mb-0 small">
              Employee ID: <strong className="text-dark me-3">{detail.EmpID || NO_DATA}</strong>
            </p>
          </div>
          <span
            style={{
              backgroundColor: statusInfo.header,
              color: "#ffffff",
              padding: "6px 20px",
              borderRadius: "20px",
              fontSize: "13px",
              fontWeight: "600",
              letterSpacing: "0.3px",
              display: "inline-block",
              whiteSpace: "nowrap",
            }}
          >
            {statusInfo.label}
          </span>
        </div>
      </div>

      <SectionCard icon="ti ti-briefcase" title="Request Information">
        <InfoRow label="Reference No" value={detail.ReferenceNumber} />
        <InfoRow label="Sequence No" value={detail.SequenceNumber} />
        <InfoRow label="Application applied for the reason" value={reasonLabel} />
        <InfoRow label="Approver Name" value={approverNameDisplay} />
        <InfoRow label="SV4 Creation Date" value={creationDisplay} />
        <InfoRow label="Organisation Key" value={organisationProjDisplay} />
      </SectionCard>

      <SectionCard icon="ti ti-id" title="Employee Details">
        <InfoRow label="Employee ID" value={detail.EmpID} />
        <InfoRow label="Employee Name" value={employeeName} />
        <InfoRow label="Designation" value={detail.Designation} />
        <InfoRow label="Department" value={detail.Department} />
        <InfoRow label="Employee Group" value={detail.EmployeeGroup} />
        <InfoRow label="Place of Posting" value={detail.PlaceOfPosting} />
        <InfoRow label="Contact No" value={detail.ContactNumber} />
        <InfoRow label="Existing card number" value={detail.ExistingCardNumber} />
      </SectionCard>

      <SectionCard icon="ti ti-user-check" title="Approver Name">
        <InfoRow label="Approver User ID" value={detail.ApproverUserID} />
        <InfoRow label="Approver Level" value={detail.ApproverLevel} />
        <InfoRow label="User Remarks" value={detail.UserRemarks} />
      </SectionCard>

      <div className="card border-0 shadow-sm mb-4">
        <div className="card-header bg-white py-3 border-bottom">
          <h5 className="card-title mb-0 fw-bold text-dark d-flex align-items-center">
            <i className="ti ti-paperclip me-2 text-danger fs-5" />
            Attached Files
          </h5>
        </div>
        <div className="card-body p-4">
          {documents.length > 0 ? (
            <div className="d-flex flex-column gap-2">
              {documents.map((file: any, idx: number) => {
                const filename = getDocumentDisplayName(file, idx);
                const fileUrl = getSv4AttachmentUrl(file);
                const isFullUrl = Boolean(fileUrl?.startsWith("http"));
                return isFullUrl ? (
                  <a
                    key={file.Guid ?? file.AttachmentId ?? idx}
                    href={fileUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="btn btn-sm btn-outline-danger d-inline-flex align-items-center gap-2 py-2 px-3 rounded-2 align-self-start"
                    title={`Open ${filename} in new tab`}
                  >
                    <i className="ti ti-file-text fs-5" />
                    <span className="fw-medium">{filename}</span>
                    <i className="ti ti-external-link ms-1" />
                  </a>
                ) : (
                  <span
                    key={file.Guid ?? file.AttachmentId ?? idx}
                    className="btn btn-sm btn-light border d-inline-flex align-items-center gap-2 py-2 px-3 rounded-2 text-muted align-self-start"
                  >
                    <i className="ti ti-file-text fs-5" />
                    <span>{filename}</span>
                  </span>
                );
              })}
            </div>
          ) : (
            <div className="text-muted">{NO_DATA}</div>
          )}
        </div>
      </div>

      <div className="card border-0 shadow-sm mb-4">
        <div className="card-header bg-white py-3 border-bottom">
          <h5 className="card-title mb-0 fw-bold text-dark d-flex align-items-center">
            <i className="ti ti-history me-2 text-danger fs-5" />
            History
          </h5>
        </div>
        <div className="card-body p-0">
          {remarksList.length > 0 ? (
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th className="ps-4">Approver Name</th>
                    <th>User ID</th>
                    <th>Remarks</th>
                    <th className="pe-4">Time Stamp</th>
                  </tr>
                </thead>
                <tbody>
                  {remarksList.map((h: any, idx: number) => (
                    <tr key={idx}>
                      <td className="ps-4 fw-medium text-dark">{h.ApproverName || NO_DATA}</td>
                      <td className="text-muted small">
                        {h.ApproverUserID != null && String(h.ApproverUserID).trim() !== ""
                          ? String(h.ApproverUserID)
                          : NO_DATA}
                      </td>
                      <td>{h.HRemarks?.trim() || NO_DATA}</td>
                      <td className="pe-4 text-muted small">
                        {formatRemarkHistoryTimestamp(h.TimeStamp) || NO_DATA}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-4 text-muted">{NO_DATA}</div>
          )}
        </div>
      </div>
    </>
  );
};

const Sv4CardDetail: React.FC = () => {
  const routes = all_routes;
  const { referenceNumber } = useParams<{ referenceNumber: string }>();
  const navigate = useNavigate();

  const [detail, setDetail] = useState<DetailData | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      if (!referenceNumber) return;
      setLoading(true);
      try {
        const data = await findOneSv4Card(decodeURIComponent(referenceNumber));
        setDetail(data);
      } catch (error: any) {
        toast.error(error?.message || "Failed to load details");
        setDetail(null);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [referenceNumber]);

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">SV4 Card Issue/Re-issue</h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.employeeDashboard}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={routes.sv4Card.list}>SV4 Card</Link>
                </li>
                <li className="breadcrumb-item active">Request Detail</li>
              </ol>
            </nav>
          </div>
          <button className="btn btn-outline-secondary" onClick={() => navigate(routes.sv4Card.list)}>
            <i className="ti ti-arrow-left me-1" />
            Back
          </button>
        </div>

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" />
            <p className="text-muted mt-2">Loading details...</p>
          </div>
        ) : !detail ? (
          <div className="text-center py-5">
            <i className="ti ti-file-off fs-1 text-muted" />
            <p className="text-muted mt-2">{NO_DATA}</p>
          </div>
        ) : (
          <DetailCards detail={detail} />
        )}
      </div>
    </div>
  );
};

export default Sv4CardDetail;
